import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DispatchService from './DispatchService';

// Public, phone-first page a buyer (คนกด) reaches from the card's "กดแล้ว แนบหลักฐาน"
// button (?t=<offer token>): the item they bought and one picture of the purchase
// confirmation. Opening it is recorded on their card; uploading closes the job
// (first proof wins). Thai, because buyers use it. Backend DISPATCH_BOT.md /proof/:token.

const clock = (d, timeZone) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone });
const nowThaiJapan = () => {
  const d = new Date();
  return `${clock(d, 'Asia/Bangkok')} เวลาไทย (${clock(d, 'Asia/Tokyo')} ญี่ปุ่น)`;
};
const MODE = { sea: { label: 'เรือ', cls: 'bg-blue-600' }, air: { label: 'แอร์', cls: 'bg-orange-500' } };

const Shell = ({ children }) => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow p-6 max-w-sm w-full text-center">{children}</div>
  </div>
);

export default function DispatchProofPage() {
  const [params] = useSearchParams();
  const token = params.get('t');
  const [job, setJob] = useState(null);
  const [closed, setClosed] = useState(null); // message when the card is no longer usable
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) { setClosed('ลิงก์ไม่ถูกต้อง'); return; }
    try {
      const res = await DispatchService.getProof(token);
      setJob(res.data.job);
    } catch (err) {
      const status = err?.response?.status;
      setClosed(status === 409 ? 'งานนี้ส่งหลักฐานไปแล้ว ขอบคุณครับ' : status === 410 ? 'งานนี้ปิดแล้ว ไม่ต้องกดชิ้นนี้ครับ' : 'เปิดหน้านี้ไม่ได้ ลองใหม่อีกครั้ง');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { setError('ต้องเป็นรูปภาพ'); return; }
    setError(null);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  // Desktop buyers paste a screenshot (Ctrl+V); phone buyers pick / shoot one.
  useEffect(() => {
    const onPaste = (e) => {
      const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith('image/'));
      if (item) pick(item.getAsFile());
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  const send = async () => {
    try {
      setBusy(true);
      setError(null);
      await DispatchService.postProof(token, file);
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410) setClosed('งานนี้ปิดแล้ว ไม่ต้องกดชิ้นนี้ครับ');
      else if (status === 409) setClosed('งานนี้ส่งหลักฐานไปแล้ว ขอบคุณครับ');
      else setError(err?.response?.data?.message || 'ส่งไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setBusy(false);
    }
  };

  if (closed) {
    return (
      <Shell>
        <div className="text-5xl mb-4">🔒</div>
        <p className="text-lg font-semibold text-gray-700">{closed}</p>
      </Shell>
    );
  }
  if (done) {
    return (
      <Shell>
        <div className="text-5xl mb-4">✅</div>
        <p className="text-xl font-bold text-green-700 mb-2">ส่งหลักฐานแล้ว ขอบคุณครับ</p>
        <p className="text-sm text-gray-500">เรียบร้อยแล้ว ปิดหน้านี้ได้เลยครับ</p>
      </Shell>
    );
  }
  if (!job) return <Shell><p className="text-gray-500">กำลังโหลด...</p></Shell>;

  const mode = MODE[job.mode] || { label: job.mode, cls: 'bg-gray-500' };
  return (
    <Shell>
      <p className="text-gray-500 text-sm">แนบหลักฐานการกด Mercari</p>
      <div className={`inline-block text-white text-2xl font-bold rounded-xl px-6 py-2 my-3 ${mode.cls}`}>{mode.label}</div>
      {job.imageUrl && <img src={job.imageUrl} alt="" className="w-40 h-40 object-cover rounded-xl mx-auto mb-3" />}
      <p className="text-2xl font-bold">¥{Number(job.priceJpy || 0).toLocaleString()}</p>
      <a href={job.itemUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm break-all">{job.itemUrl}</a>
      <p className="text-xs text-gray-400 mt-1 mb-4">ตอนนี้ {nowThaiJapan()}</p>

      <label className="block rounded-xl border-2 border-dashed border-gray-300 p-4 cursor-pointer hover:border-blue-400">
        <input type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        {preview
          ? <img src={preview} alt="หลักฐาน" className="max-h-56 mx-auto rounded-lg" />
          : <span className="text-gray-600">📷 แตะเพื่อเลือกรูปหน้ายืนยันการซื้อ<br /><span className="text-xs text-gray-400">(คอมพิวเตอร์: วางรูปด้วย Ctrl+V ได้)</span></span>}
      </label>
      {preview && <button type="button" className="text-xs text-gray-500 underline mt-1" onClick={() => { setFile(null); setPreview(null); }}>เปลี่ยนรูป</button>}

      <button
        type="button"
        disabled={!file || busy}
        onClick={send}
        className="mt-4 w-full rounded-xl py-4 text-lg font-semibold text-white bg-green-600 disabled:opacity-40"
      >
        {busy ? 'กำลังส่ง...' : 'ส่งหลักฐาน'}
      </button>
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      <p className="text-xs text-gray-400 mt-4">ส่งได้ครั้งเดียว · ถ้ายังไม่ได้กด ไม่ต้องส่งครับ</p>
    </Shell>
  );
}
